import { Box, Typography } from '@mui/material';
import type { AvailableOption, SubstepCopy } from '@/types';
import { OptionSolutionCard } from './OptionSolutionCard';
import { DoorGlyph } from './doorGlyphs';
import { refineGlyph } from './techGlyphs';
import { selectSingle, toggleVariant, variantOptions } from '@/utils/options.utils';

// Substep a SCELTA SINGOLA: la variante SOSTITUISCE la base → pulsante SELEZIONA/SELEZIONATO. Tutto
// il resto è ADD-ON additivo (per-posizione o all-or-nothing) → AGGIUNGI/AGGIUNTO+ELIMINA, come il
// legacy (LAYOUT: Distribuzione/Bagno/Cucina; TECNOLOGIA: Camera Principale luci). Il BE non espone la
// cardinalità → lista esplicita per key. NB: NON dedurre dalla presenza di `positions` (le opzioni
// TECNOLOGIA usano Option.Position e alcune sono all-or-nothing).
const SINGLE_CHOICE_KEYS = new Set(['SceltaLayout', 'Bagno', 'Cucina', 'LuciComodinoCameraPrincipale']);
// SmartHome: il BE ritorna il Gateway (position 6) ma va NASCOSTO (auto-iniettato al salvataggio).
const HIDDEN_POSITION_BY_SUBSTEP: Record<string, number> = { SmartHome: 6 };

interface SolutionsColumnProps {
  options: AvailableOption[];
  /** Copy statica del substep (tagline/warning/note/caption) dal BE. Null se non mappata. */
  copy: SubstepCopy | null;
  /** Substep.Key corrente (per le regole di cardinalità/visibilità specifiche di TECNOLOGIA). */
  substepKey: string | null;
  isBusy: boolean;
  /** Applica il nuovo insieme di opzioni del substep (con anteprima invalidazioni gestita a monte). */
  onApply: (optionIds: number[]) => void;
}

/**
 * Colonna "SOLUZIONI AGGIUNTIVE/ALTERNATIVE": le opzioni variante (OGN!=0) del substep. Instrada la
 * cardinalità sui dati di dominio (per-posizione vs scelta-singola) e, al toggle, ricostruisce
 * l'insieme `options[]` da salvare tramite gli helper puri.
 */
export function SolutionsColumn({ options, copy, substepKey, isBusy, onApply }: SolutionsColumnProps) {
  // Add-on (AGGIUNGI) per default; scelta-singola (SELEZIONA) solo per i substep esclusivi noti.
  const isAddOn = !(substepKey != null && SINGLE_CHOICE_KEYS.has(substepKey));
  const hiddenPosition = substepKey != null ? HIDDEN_POSITION_BY_SUBSTEP[substepKey] : undefined;
  const variants = variantOptions(options).filter(
    (v) => hiddenPosition === undefined || !v.positions.includes(hiddenPosition),
  );

  if (variants.length === 0) {
    return (
      <Box>
        <ColumnHeading perPosition={isAddOn} />
        <ColumnIntro copy={copy} glyph={null} />
        <Typography variant="body2" color="text.secondary">
          Nessuna soluzione aggiuntiva disponibile per questo passo. La configurazione di partenza è
          già completa.
        </Typography>
      </Box>
    );
  }

  const handleToggle = (option: AvailableOption): void => {
    const next = isAddOn
      ? toggleVariant(options, option.optionId)
      : selectSingle(option.optionId);
    onApply(next);
  };

  // Tipo offerto = glifo (raffinato) della prima variante, mostrato grande in cima come nel legacy.
  const offeredVariant = variants.find((v) => v.glyph) ?? null;
  const offeredGlyph = offeredVariant ? refineGlyph(offeredVariant) : null;

  return (
    <Box>
      <ColumnHeading perPosition={isAddOn} />
      <ColumnIntro copy={copy} glyph={offeredGlyph} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {variants.map((option) => (
          <OptionSolutionCard
            key={option.optionId}
            option={option}
            mode={isAddOn ? 'add' : 'select'}
            caption={copy?.caption ?? null}
            selected={option.isAnswered}
            isBusy={isBusy}
            onToggle={handleToggle}
          />
        ))}
      </Box>
    </Box>
  );
}

/**
 * Intro della colonna (come `ConfiguratorIntro` legacy): tagline serif-corsivo, box "Attenzione!"
 * (cornice arancio/crema) per il vincolo tecnico, nota neutra. Mostrato una sola volta, sopra le card.
 */
function ColumnIntro({ copy, glyph }: { copy: SubstepCopy | null; glyph: string | null }) {
  const hasCopy = copy && (copy.tagline || copy.warning || copy.note);
  if (!hasCopy && !glyph) return null;
  return (
    <Box sx={{ mb: 2 }}>
      {glyph && <DoorGlyph name={glyph} height={120} label="Render del tipo offerto" />}
      {copy?.tagline && (
        <Typography
          sx={{
            fontFamily: '"miller", serif',
            fontStyle: 'italic',
            fontSize: '1.125rem',
            color: 'text.primary',
            mb: copy?.warning || copy?.note ? 1.5 : 0,
          }}
        >
          {copy?.tagline}
        </Typography>
      )}
      {copy?.warning && (
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'primary.main',
            bgcolor: (theme) => theme.custom.colors.brownGrey1,
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mb: copy.note ? 1 : 0,
          }}
        >
          <Typography variant="body2" sx={{ color: 'primary.main' }}>
            <Box component="span" sx={{ fontWeight: 700 }}>
              Attenzione!
            </Box>{' '}
            {copy.warning}
          </Typography>
        </Box>
      )}
      {copy?.note && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {copy.note}
        </Typography>
      )}
    </Box>
  );
}

/**
 * Titolo colonna: "SOLUZIONI AGGIUNTIVE" per gli add-on per-posizione, "SOLUZIONI ALTERNATIVE" per le
 * scelte-singole (sostituzione della base) — come nel legacy (secondColumnFirstRowTitle/SecondRowTitle).
 */
function ColumnHeading({ perPosition }: { perPosition: boolean }) {
  return (
    <Typography
      variant="overline"
      sx={{ color: 'text.primary', letterSpacing: '0.08em', fontWeight: 700, display: 'block', mb: 1.5 }}
    >
      {perPosition ? 'Soluzioni aggiuntive' : 'Soluzioni alternative'}
    </Typography>
  );
}
